import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BaseReportComponentProps, MdaReportType, MdaReportTypeField, MdaReportTypes } from '../../store/types';
import { DateRange } from 'src/store';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, FormControlLabel, Grid, TextField } from '@mui/material';
import { WO2Checkbox } from 'src/common';
import React from 'react';
import { ExpandMore } from '@mui/icons-material';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ReportGroupingOption } from '../ReportGroupingOption';
import { ClientPortfolio } from '../../../common/store';

export interface ProjectedIncomeCashflowProps extends BaseReportComponentProps {
  setProjectedIncomeCashflowSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setAsAtDateProjectedIncomeCashflowParameter: ActionCreatorWithPayload<DateRange>;
  setProjectedIncomeCashflowPortfolioIdParameter?: ActionCreatorWithPayload<number, string>;
  expandProjectedIncomeCashflow: ActionCreatorWithoutPayload<string>;
  clientPortfolios: ClientPortfolio[];
}

export const ProjectedIncomeCashflow = ({
  parameters,
  setProjectedIncomeCashflowSelectedParameter,
  setAsAtDateProjectedIncomeCashflowParameter,
  setProjectedIncomeCashflowPortfolioIdParameter,
  createReportSortRequest,
  clientPortfolios,
  expandProjectedIncomeCashflow,
  setGroupConsolidatedOption,
  disablePortfolioSelect,
}: ProjectedIncomeCashflowProps) => {
  const handleClickDate = (dates: DateRange) => {
    setAsAtDateProjectedIncomeCashflowParameter(dates);
  };

  const handleSelectPortfolio = (portfolio: ClientPortfolio | null) => {
    if (!setProjectedIncomeCashflowPortfolioIdParameter) return;
    setProjectedIncomeCashflowPortfolioIdParameter(portfolio?.investmentProgramId ?? -1);
  };

  const handleClickSelect = () => {
    setProjectedIncomeCashflowSelectedParameter(!parameters.selectedReports?.projectedIncomeCashflow.selected);
    createReportSortRequest(
      !parameters.selectedReports?.projectedIncomeCashflow.selected,
      MdaReportTypes.ProjectedIncomeCashflow.id,
      MdaReportTypes.ProjectedIncomeCashflow.displayName
    );
  };

  return (
    <Accordion
      onChange={expandProjectedIncomeCashflow}
      expanded={parameters.selectedReports?.projectedIncomeCashflow.expanded || false}
      elevation={0}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          aria-label="Acknowledge"
          sx={{ flex: 1 }}
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox color="primary" checked={parameters.selectedReports?.projectedIncomeCashflow.selected} onChangeHandler={handleClickSelect} />
          }
          label={MdaReportType.ProjectedIncomeCashflow}
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: '',
              dateTo: parameters?.selectedReports.projectedIncomeCashflow?.asAtDate,
              dateRangeType: parameters?.selectedReports.projectedIncomeCashflow?.asAtDateType,
            }}
            dateOnlyLabel="As At"
            setDateRange={handleClickDate}
            isDateOnly
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={1}>
          {setProjectedIncomeCashflowPortfolioIdParameter && !disablePortfolioSelect && (
            <Grid item>
              <Autocomplete
                noOptionsText="No items to select"
                id="projected-income-cashflow-multiselect"
                fullWidth
                limitTags={1}
                value={
                  clientPortfolios.find(
                    (portfolio) => portfolio.investmentProgramId === parameters.selectedReports.projectedIncomeCashflow.portfolioId
                  ) || null
                }
                options={clientPortfolios}
                onChange={(_e: React.SyntheticEvent<Element, Event>, value: ClientPortfolio | null) => handleSelectPortfolio(value)}
                getOptionLabel={(option: ClientPortfolio) => option.investmentServiceName}
                renderInput={(params) => <TextField {...params} label="Select a Portfolio" margin="normal" fullWidth />}
                isOptionEqualToValue={(option: ClientPortfolio, value: ClientPortfolio) => option.investmentProgramId === value.investmentProgramId}
              />
            </Grid>
          )}
          {setGroupConsolidatedOption && (
            <Grid item>
              <ReportGroupingOption
                value={parameters.selectedReports.projectedIncomeCashflow.groupOption}
                setGroupConsolidatedOption={setGroupConsolidatedOption}
                report={MdaReportTypeField.ProjectedIncomeCashflow}
              />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
